import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type {
  AppSelectOption,
  SelectPaginatedData,
} from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectPaginatedFetcherParams } from '@shared/ui/selects'

import { useListsStore } from '@shared/model'
import { useAppSelectPaginated } from '@shared/ui/selects'

import { useTutorsListApi } from '../api/list.hook'

interface FilterTutorsOptions {
  selectedIds: Ref<number[]>
  /** Only active tutors, as some legacy filters listed them. */
  active?: boolean
  /** Only tutors with a paid label. */
  paid?: boolean
  /** The legacy "Name (Paid label)" option text instead of the API's "Name (#id)". */
  withPaidLabel?: boolean
}

interface TutorListOption extends AppSelectOption<number> {
  paid_label?: string | null
}

const ID_SUFFIX = / \(#\d+\)$/

/** Legacy named a tutor by the paid label and added the id only when two loaded tutors share a name. */
const toPaidLabelOptions = (
  options: TutorListOption[],
  paidLabels: Record<string, string>,
): AppSelectOption<number>[] => {
  const names = options.map(option => option.text.replace(ID_SUFFIX, ''))

  return options.map((option, index) => {
    const name = names[index] ?? option.text
    const label = option.paid_label ? paidLabels[option.paid_label] ?? option.paid_label : '-'
    const isDuplicated = names.filter(item => item === name).length > 1

    return {
      value: option.value,
      text: isDuplicated ? `${name} (${label}) (id: ${option.value})` : `${name} (${label})`,
    }
  })
}

interface FilterTutorsReturn {
  refSelectedIds: WritableComputedRef<number[] | undefined>
  tutorOptions: ComputedRef<AppSelectOption<number>[]>
  selectedTutorOptions: ComputedRef<AppSelectOption<number>[]>
  tutorHasMore: ComputedRef<boolean>
  tutorLoading: Readonly<Ref<boolean>>
  onTutorSearch: (query: string) => void
  onTutorLoadMore: (query: string) => void
  onTutorOpen: () => void
}

/**
 * A searchable tutor multi-select over `admin-api.tutors.list`. Tutor ids that arrive from the URL are
 * sent as `preselected`, so their labels resolve without the admin opening the dropdown.
 */
export const useFilterTutors = ({
  selectedIds,
  active = false,
  paid = false,
  withPaidLabel = false,
}: FilterTutorsOptions): FilterTutorsReturn => {
  const listsStore = useListsStore()
  const selectedCache = ref<AppSelectOption<number>[]>([])

  const refSelectedIds = computed<number[] | undefined>({
    get: () => selectedIds.value,
    set: (value) => {
      selectedIds.value = value ?? []
    },
  })

  const fetcher = ({ query, page, signal }: AppSelectPaginatedFetcherParams): Promise<SelectPaginatedData | null> =>
    useTutorsListApi({
      query,
      page,
      signal,
      active,
      paid,
      preselected: selectedIds.value.length ? [...selectedIds.value] : undefined,
    })

  const {
    options,
    hasMore: tutorHasMore,
    loading: tutorLoading,
    onSearch: onTutorSearch,
    onLoadMore: onTutorLoadMore,
    onOpen: onTutorOpen,
    refresh,
  } = useAppSelectPaginated(fetcher, { lazy: true })

  const tutorOptions = computed<AppSelectOption<number>[]>(() => (withPaidLabel
    ? toPaidLabelOptions(options.value as TutorListOption[], listsStore.getLists?.tutor.paid_label ?? {})
    : options.value as AppSelectOption<number>[]))

  const selectedTutorOptions = computed<AppSelectOption<number>[]>(() =>
    selectedCache.value.filter(option => selectedIds.value.includes(option.value)),
  )

  const setCacheSelected = (): void => {
    const ids = selectedIds.value
    const kept = selectedCache.value.filter(option => ids.includes(option.value))
    const added = tutorOptions.value.filter(option =>
      ids.includes(option.value) && !kept.some(item => item.value === option.value),
    )
    selectedCache.value = [...kept, ...added]
  }

  // A deleted tutor never comes back from `preselected`; asking again would only reset the search on every pick.
  const requestedIds = new Set<number>()

  const setMissingLabelsLoaded = (ids: number[]): void => {
    const known = [...tutorOptions.value, ...selectedCache.value].map(option => option.value)
    const missing = ids.filter(id => !known.includes(id) && !requestedIds.has(id))

    if (!missing.length) return

    missing.forEach(id => requestedIds.add(id))
    void refresh()
  }

  watch([selectedIds, tutorOptions], setCacheSelected, { immediate: true })

  watch(selectedIds, setMissingLabelsLoaded, { immediate: true })

  return {
    refSelectedIds,
    tutorOptions,
    selectedTutorOptions,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
  }
}
